/**
 * "Delete this release?" confirmation for Publisher dashboard removal.
 *
 * Reuses the shared destructive-dialog shell and keeps the action truthful: this removes the
 * release from Lyrixa's dashboard, not from DSPs. The actual purge is delayed behind the
 * 24-hour undo window owned by publisherStore.
 */
import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import { TextField } from '../forms';
import { c } from '../../styles/theme';
import type { PublisherRelease } from '../../types/domain';

const BOX_STYLE = {
  width: 15,
  height: 15,
  margin: 0,
  flexShrink: 0,
  marginTop: 1,
  accentColor: c.accentFill,
  cursor: 'pointer',
} as const;

export interface DeleteReleaseModalProps {
  release: PublisherRelease | null;
  onClose: () => void;
  onConfirm: () => void;
}

export default function DeleteReleaseModal({
  release,
  onClose,
  onConfirm,
}: DeleteReleaseModalProps) {
  const [understandsDspAvailability, setUnderstandsDspAvailability] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');

  const title = release?.title.trim() || 'Untitled release';
  const confirmMatchesTitle = confirmTitle.trim() === title;
  const confirmHintId = 'lyx-delete-release-confirm-hint';

  const reset = () => {
    setUnderstandsDspAvailability(false);
    setConfirmTitle('');
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <ConfirmDialog
      open={Boolean(release)}
      onClose={close}
      title="Delete this release?"
      icon={<Trash2 size={19} color={c.danger} />}
      destructive
      confirmLabel="Delete release"
      confirmIcon={<Trash2 size={13} />}
      confirmDisabled={!understandsDspAvailability || !confirmMatchesTitle}
      onConfirm={() => {
        onConfirm();
        reset();
      }}
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <div style={{ display: 'grid', gap: 8 }}>
          <p style={{ fontSize: 12, color: c.text2, margin: 0, lineHeight: 1.55 }}>
            This removes <span style={{ color: c.text1 }}>&ldquo;{title}&rdquo;</span> from your Lyrixa dashboard only.
          </p>
          <p style={{ fontSize: 12, color: c.text2, margin: 0, lineHeight: 1.55 }}>
            Your music stays live on Spotify, Apple Music, and other DSPs, and royalties continue to accrue.
          </p>
          <p style={{ fontSize: 12, color: c.text2, margin: 0, lineHeight: 1.55 }}>
            Request Takedown is a separate path if you need the release removed from platforms.
          </p>
          <p style={{ fontSize: 12, color: c.text2, margin: 0, lineHeight: 1.55 }}>
            You can undo this dashboard removal for 24 hours after confirming.
          </p>
        </div>

        <div
          style={{
            border: `1px solid ${c.border}`,
            background: c.surface2,
            borderRadius: 8,
            padding: '10px 11px',
          }}
        >
          <label
            htmlFor="lyx-delete-release-acknowledgement"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 9,
              fontSize: 11.5,
              lineHeight: 1.55,
              color: c.text2,
              cursor: 'pointer',
            }}
          >
            <input
              id="lyx-delete-release-acknowledgement"
              type="checkbox"
              checked={understandsDspAvailability}
              onChange={(event) => setUnderstandsDspAvailability(event.target.checked)}
              aria-required="true"
              style={BOX_STYLE}
            />
            <span>
              I understand my music stays live on DSPs.
              <span
                style={{
                  fontSize: 9,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: c.accent,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  marginLeft: 6,
                }}
              >
                Required
              </span>
            </span>
          </label>
        </div>

        <div>
          <label
            htmlFor="lyx-delete-release-confirm-title"
            style={{ fontSize: 11, color: c.text2, display: 'block', margin: '0 0 6px' }}
          >
            Type <span style={{ color: c.text1, fontFamily: 'IBM Plex Mono' }}>{title}</span> to confirm
          </label>
          <TextField
            id="lyx-delete-release-confirm-title"
            value={confirmTitle}
            onChange={setConfirmTitle}
            placeholder={title}
            ariaLabel="Release title confirmation"
            mono
            invalid={confirmTitle.trim().length > 0 && !confirmMatchesTitle}
            describedBy={confirmHintId}
            marginBottom={0}
            style={{ padding: '8px 10px' }}
          />
          <p
            id={confirmHintId}
            style={{
              margin: '6px 0 0',
              fontSize: 11,
              lineHeight: 1.45,
              color:
                confirmTitle.trim().length > 0 && !confirmMatchesTitle ? c.danger : c.text2,
            }}
          >
            Enter the exact release title to enable deletion.
          </p>
        </div>
      </div>
    </ConfirmDialog>
  );
}
